import { Box, Checkbox, HStack, Select, Text, Tooltip } from '@chakra-ui/react'
import type { AddonProduct, InsuranceParamsDto, Offer, OfferAddon } from 'BrokerTsLib/src/dtos'
import { Risk } from 'BrokerTsLib/src/enums'
import moment from 'moment'
import { useEffect, useState } from 'react'

import { QuestionMarkIcon } from '@/components/QuestionMarkIcon'

import { useInsuranceParams } from '../../../context/InsuranceParamsProvider'
import { useOffersAddonContext } from '../../../context/OffersAddonProvider'
import { useChangePrice, useGetAddonParamValues, useSelectOfferAddon } from '../../../hooks/useOfferAddon'
import { OfferPart } from './OfferPart'

export interface AllianzAddonGapProps {
  offer: Offer,
  allowReplacementGap: boolean,
  allowFollowingGap: boolean
}

export const AllianzAddonGap = ({ offer, allowReplacementGap, allowFollowingGap }: AllianzAddonGapProps) => {

  const { insuranceParams, isDisabled } = useInsuranceParams()
  const insurancePramsDto = insuranceParams as InsuranceParamsDto

  const { offers: addonOffers, refetchOffers: refetchAddonOffers, updateOffer: updateAddonOffer } = useOffersAddonContext()
  const allGapOffers = addonOffers.filter(x => x.riskId == Risk.GAP) ?? []

  let gapOffers: OfferAddon[] = []
  let gapOffer: OfferAddon | null = null

  if (allowFollowingGap) {
    gapOffers = allGapOffers.filter(x => x.params?.isFollowGap)
    gapOffer = gapOffers.find(x => x.linkedOfferId?.includes(offer.offerId) ?? false)
            ?? (gapOffers.find(x => (60 - parseInt(x.params?.parameterValues.find(p => p.name == 'Term')?.value ?? '0')) == 24)
                ?? (gapOffers.length > 0 ? gapOffers[0] : null))
  }

  if (allowReplacementGap) {
    gapOffers = allGapOffers.filter(x => !x.params?.isFollowGap)
    gapOffer = gapOffers.find(x => x.linkedOfferId?.includes(offer.offerId) ?? false)
            ?? (gapOffers.find(x => x.params?.parameterValues.find(p => p.name == 'Term')?.value == '24')
                ?? (gapOffers.length > 0 ? gapOffers[0] : null))
  }

  const [gapPossibleTerms, setGapPossibleTerms] = useState<AddonProduct[]>([])
  const [checkedGap, setCheckedGap] = useState(gapOffer?.linkedOfferId?.includes(offer.offerId) ?? false)

  const { mutateAsync: getAddonParamValues } = useGetAddonParamValues()
  const { mutateAsync: selectOfferAddon } = useSelectOfferAddon()
  const { mutateAsync: changePrice } = useChangePrice()

  useEffect(() => {
    if (gapOffer)
      getAddonParamValues({
        mainOfferId: offer.offerId,
        addonOfferId: gapOffer.offerId,
        paramName: 'Term'
      }).then(({ data }) => setGapPossibleTerms(data))
  }, [gapOffer?.offerId])

  useEffect(() => {
    setCheckedGap(gapOffer?.linkedOfferId?.includes(offer.offerId) ?? false)
  }, [gapOffer])

  const handleSelectAddonOffer = (addonOfferId?: number, term?: string) => {
    return selectOfferAddon({ mainOfferId: offer.offerId, addonOfferId, term })
      .then(() => refetchAddonOffers({ force: false }))
  }

  const defendGapEditPriceTooltip = 'Nastavení ceny má vliv na provizi. Při zadání nejnižší možné částky je provize 0%'

  if (allowFollowingGap && allowReplacementGap || !allowFollowingGap && !allowReplacementGap || !gapOffer)
    return null

  return (
    <Box display='grid' rowGap={1} sx={{
      p: 2,
      pr: 0,
      gap: 3,
      borderBottom: '1px solid',
      borderBottomColor: 'neutral.50',
      _last: {
        borderBottom: 'none',
      },
    }}>

      <Box>
        <HStack sx={{ fontSize: 'sm', mb: 2 }}>
          {allowFollowingGap && (
            <Tooltip label={<Text>Navazující od {moment(insurancePramsDto.generalParams.insuranceStartDate).add(24, 'M').format('D. M. YYYY')}</Text>} placement="top" hasArrow>
              <Text>GAP navazující</Text>
            </Tooltip>
          )}

          {allowReplacementGap && (
            <Text>GAP</Text>
          )}
        </HStack>

        <OfferPart
          label={
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto auto',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'no-wrap',
              }}>

              <Checkbox
                size="sm"
                sx={{ flexBasis: '100%' }}
                isChecked={checkedGap}
                disabled={isDisabled}
                onChange={e => {
                  setCheckedGap(e.target.checked)

                  if (e.target.checked)
                    handleSelectAddonOffer(gapOffer?.offerId, gapOffer?.params?.parameterValues.find(x => x.name == 'Term')?.value)
                  else
                    handleSelectAddonOffer()
                }}
              >
                {gapOffer.insurerName}
              </Checkbox>

              <Select
                variant='filled'
                size="xs"
                disabled={isDisabled}
                value={gapOffer.params?.parameterValues.find(x => x.name == 'Term')?.value}
                onChange={e => handleSelectAddonOffer(gapOffer?.offerId, e.target.value)}
              >
                {gapPossibleTerms.map(x => <option key={x.value} value={x.value}>{x.text}</option>)}
              </Select>

              {gapOffer.productDescription &&
                                <Tooltip label={gapOffer.productDescription} placement="top" hasArrow>
                                  <QuestionMarkIcon />
                                </Tooltip>
              }
            </Box>
          }
          price={gapOffer.pricePerYear}
          unavailableText='Nelze sjednat'
          editableProps={{
            isEditable: !!gapOffer.priceMin && !!gapOffer.priceMax && checkedGap && !isDisabled,
            priceMin: gapOffer.priceMin,
            priceMax: gapOffer.priceMax,
            price: gapOffer.pricePerYear,
            onChange: (price: number, successAction: () => void, errorAction: (error: string) => void) => {
              changePrice({ offerId: gapOffer!.offerId, price })
                .then(({ data: updatedOffer }) => {
                  successAction()
                  updateAddonOffer(updatedOffer)
                }).catch((reason) => {
                  let error = 'Nelze uložit změnu ceny'
                  if (reason.response.status == 400) {
                    error = reason.response.data
                  }
                  errorAction(error)
                })
            },
            tooltip: defendGapEditPriceTooltip
          }}
          errors={[]}
        />
      </Box>
    </Box>
  )
}
